import { useState, useEffect, useRef, useCallback } from 'react';

const useWebSocket = (url, options = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  const ws = useRef(null);
  const reconnectTimer = useRef(null);
  
  const {
    onMessage = () => {},
    onConnect = () => {},
    onDisconnect = () => {},
    onError = () => {},
    reconnectInterval = 3000,
    maxReconnectAttempts = 5,
    filters = {}
  } = options;

  const connect = useCallback(() => {
    try {
      // Use environment variable for backend URL, but convert HTTP to WS  
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let wsUrl;
      
      if (url.startsWith('ws://') || url.startsWith('wss://')) {
        wsUrl = url;
      } else {
        // Convert HTTP/HTTPS URL to WebSocket URL
        wsUrl = backendUrl.replace(/^http/, 'ws') + url;
      }
      
      ws.current = new WebSocket(wsUrl);
      
      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setError(null);
        setReconnectAttempts(0);
        onConnect();
        
        // Send initial filters if any
        if (Object.keys(filters).length > 0) {
          sendMessage({
            type: 'set_filters',
            filters: filters
          });
        }
      };
      
      ws.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          setMessages(prev => [...prev.slice(-99), message]); // Keep last 100 messages
          onMessage(message);
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };
      
      ws.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        onDisconnect();
        
        // Attempt reconnection if within limits
        if (reconnectAttempts < maxReconnectAttempts) {
          reconnectTimer.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connect();
          }, reconnectInterval);
        }
      };
      
      ws.current.onerror = (err) => {
        console.error('WebSocket error:', err);
        setError(err);
        onError(err);
      };
      
    } catch (err) {
      console.error('Error creating WebSocket connection:', err);
      setError(err);
    }
  }, [url, onMessage, onConnect, onDisconnect, onError, reconnectInterval, maxReconnectAttempts, reconnectAttempts, filters]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
    }
    if (ws.current) {
      ws.current.close();
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Cannot send message:', message);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    messages,
    error,
    sendMessage,
    clearMessages,
    reconnectAttempts,
    maxReconnectAttempts
  };
};

export default useWebSocket;